import { Input, InputNumber } from '@seiue/ui'
import { cloneDeep, isArray } from '@seiue/util'
import { useAtomValue } from 'jotai'
import React, { useContext } from 'react'
import styled from 'styled-components'

import { CircledNumber } from 'packages/components/CircledNumber'
import { GrowpIcon } from 'packages/components/GrowpIcon'
import { RichTextEditor } from 'packages/components/RichText'
import { PaperQuestion, QuestionTypeEnum } from 'packages/sdks-next/growp'
import { AnyType } from 'packages/types'

import { WaringText } from '@/components/Styled'
import { EditPaperContext } from '@/features/test-paper/pages/EditPaper/context'
import { JudgeMode } from '@/features/test-paper/types'
import { getTheme } from '@/utils/theme'

import { isValidQuestionFieldAtom } from '../../atom'
import {
  formatFillInBlankQuestion,
  getTreeNodeId,
  validateFillInBlank,
} from '../../utils'

import { EditAnswerProps } from './index'

/**
 * 填空题答案 - 编辑
 *
 * @param props - 参数
 * @returns React.ReactNode
 */
export const EditFullInBlankAnswer: React.FC<EditAnswerProps> = (props) => {
  const { paper, limitLevel } = useContext(EditPaperContext)
  const theme = getTheme()
  const { answers, onEditAnswer } = props
  const isValidField = useAtomValue(isValidQuestionFieldAtom)

  const getStandardAnswer = (question: PaperQuestion) => {
    return isArray(question.standardAnswers) &&
      question.standardAnswers.length > 0
      ? question.standardAnswers[0]
      : ''
  }

  const onChangeAnswer = (
    question: PaperQuestion,
    index: number,
    v: string
  ) => {
    const preAnswers = question.standardAnswers
      ? [...question.standardAnswers]
      : ['']
    preAnswers.splice(0, 1, v)
    const preQuestions = cloneDeep(answers.subQuestions)
    preQuestions.splice(index, 1, {
      ...question,
      standardAnswers: preAnswers,
      score: paper.showScore ? question.score : question.score || '0',
    })
    onEditAnswer({
      ...answers,
      subQuestions: preQuestions,
    })
  }

  const validate = validateFillInBlank(answers.subQuestions, !!paper.showScore)
  return (
    <Wrapper>
      {answers.subQuestions.map((question, index) => {
        return (
          <QuestionItem key={question.id || index}>
            <div>
              <MainAnswer>
                <CircledNumber number={index + 1} />
                {answers.mainQuestion?.settings?.judgeMode ===
                JudgeMode.Manual ? (
                  <RichTextEditor
                    readOnly={limitLevel > 0}
                    minHeight={32}
                    value={getStandardAnswer(question)}
                    onChange={(v) => onChangeAnswer(question, index, v)}
                    excludeKeys={[
                      'headerSelect',
                      'group-upload',
                      'insertLink',
                      'fullScreen',
                    ]}
                    insertKeys={{
                      index: 17,
                      keys: ['uploadImage', 'insertFormula'],
                    }}
                  />
                ) : (
                  <Input
                    disabled={limitLevel > 0}
                    addonAfter={
                      answers.settings.judgeMode === JudgeMode.Manual ? null : (
                        <div
                          style={{
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            // 初始添加了标准答案选项，还未填写答案，所以用空字符串表示
                            const curAnswers = question.standardAnswers || ['']
                            curAnswers.push('')
                            const preQuestions = answers.subQuestions
                            preQuestions.splice(index, 1, {
                              ...question,
                              standardAnswers: curAnswers,
                            })
                            onEditAnswer({
                              ...answers,
                              subQuestions: [...preQuestions],
                            })
                          }}
                        >
                          <GrowpIcon
                            name="Plus"
                            size={12}
                            text="备选答案"
                          />
                        </div>
                      )
                    }
                    value={getStandardAnswer(question)}
                    onChange={(e) => {
                      const v = e.target.value || ''
                      onChangeAnswer(question, index, v)
                    }}
                  />
                )}
                {paper.showScore ? (
                  <InputNumber
                    disabled={limitLevel > 1}
                    stringMode={true}
                    addonAfter="分"
                    controls={false}
                    value={question.score}
                    onChange={(v) => {
                      const preQuestions = [...answers.subQuestions]
                      preQuestions.splice(index, 1, { ...question, score: v })
                      onEditAnswer({
                        ...answers,
                        subQuestions: preQuestions,
                      })
                    }}
                  />
                ) : null}
                <div
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    const curAnswers = question.standardAnswers || ['']
                    curAnswers.splice(0, 1)
                    const preQuestions = answers.subQuestions
                    preQuestions.splice(index, 1)
                    onEditAnswer({
                      ...answers,
                      subQuestions: [...preQuestions],
                    })
                  }}
                >
                  <GrowpIcon
                    name="Delete"
                    size={16}
                  />
                </div>
              </MainAnswer>
              {validate || !isValidField ? null : (
                <ValidateMsgWrapper>
                  <WaringText>
                    {answers.subQuestions.length > 0 &&
                    !getStandardAnswer(question)
                      ? `答案不能为空`
                      : null}
                  </WaringText>
                  <WaringText>
                    {answers.subQuestions.length > 0 && !question.score
                      ? `分数不能为空`
                      : null}
                  </WaringText>
                </ValidateMsgWrapper>
              )}
            </div>
            {question.standardAnswers?.map((answer, answerIndex) => {
              if (answerIndex < 1) return
              return (
                <div key={`${index}${answerIndex}`}>
                  <SubAnswer
                    key={`${index}${answerIndex}`}
                    $showScore={!!paper.showScore}
                  >
                    <Input
                      disabled={limitLevel > 0}
                      value={answer}
                      onChange={(e) => {
                        const v = e.target.value || ''
                        const preAnswers = question.standardAnswers
                          ? [...question.standardAnswers]
                          : ['']
                        preAnswers.splice(answerIndex, 1, v)
                        const preQuestions = [...answers.subQuestions]
                        preQuestions.splice(index, 1, {
                          ...question,
                          standardAnswers: preAnswers,
                        })
                        onEditAnswer({
                          ...answers,
                          subQuestions: preQuestions,
                        })
                      }}
                    />
                    <div
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const curAnswers = question.standardAnswers || ['']
                        curAnswers.splice(answerIndex, 1)
                        const preQuestions = answers.subQuestions
                        preQuestions.splice(index, 1, {
                          ...question,
                          standardAnswers: curAnswers,
                        })
                        onEditAnswer({
                          ...answers,
                          subQuestions: [...preQuestions],
                        })
                      }}
                    >
                      <GrowpIcon
                        name="Delete"
                        size={16}
                      />
                    </div>
                  </SubAnswer>
                  <div style={{ paddingLeft: '30px' }}>
                    {validate || !isValidField ? null : (
                      <WaringText>
                        {answers.subQuestions.length > 0 && !answer
                          ? `答案不能为空`
                          : null}
                      </WaringText>
                    )}
                  </div>
                </div>
              )
            })}
          </QuestionItem>
        )
      })}

      <div
        onClick={() => {
          const treeNodeId = getTreeNodeId(
            answers.mainQuestion
              ? [answers.mainQuestion, ...answers.subQuestions]
              : answers.subQuestions
          )
          const newQuestion = {
            type: QuestionTypeEnum.FillInBlank,
            options: [],
            standardAnswers: [''],
            parentId:
              answers.mainQuestion?.id || answers.mainQuestion?.treeNodeId || 1,
            treeNodeId: treeNodeId,
            treeNodePid:
              answers.mainQuestion?.id || answers.mainQuestion?.treeNodeId || 1,
          } as AnyType
          onEditAnswer({
            ...answers,
            subQuestions: formatFillInBlankQuestion([
              ...answers.subQuestions,
              newQuestion,
            ]),
          })
        }}
        style={{
          marginTop: isValidField ? '6px' : '0px',
          paddingLeft: answers.subQuestions.length > 0 ? '26px' : '0px',
          width: '110px',
          display: limitLevel > 0 ? 'none' : 'block',
        }}
      >
        <GrowpIcon
          name="Plus"
          size={14}
          text="添加选项"
          color={theme.brand._1}
          wrapperStyle={{
            cursor: 'pointer',
          }}
        />
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  &&& {
    .ant-input-group-addon {
      width: 20px;
    }
    .ant-input-number-group {
      width: 84px;
    }
    .ant-input-number-input {
      padding-right: 0;
    }
  }
`

const QuestionItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const MainAnswer = styled.div`
  width: 100%;
  min-height: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`

const SubAnswer = styled.div<{ $showScore: boolean }>`
  width: 100%;
  height: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: ${(props) => (props.$showScore ? '0 92px 0 26px' : '0 0 0 26px')};
`

const ValidateMsgWrapper = styled.div`
  width: 100%;
  padding: 0 30px;
  display: flex;
  justify-content: space-between;
  align-content: center;
`
